import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  IconButton,
  LinearProgress,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { closeBugReportModal, selectBugReportOpen } from '../../redux/bugReport/bugReportSlice';
import { sendBugReport } from '../../services/bugReportService';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export default function BugReportModal() {
  const dispatch = useDispatch();
  const open = useSelector(selectBugReportOpen);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Low');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [epicId, setEpicId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setAttachments((prev) => [...prev, ...files]);
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Title is required.',
      });
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    const payload = {
      title,
      description,
      priority,
      epic_id: epicId,
      attachments,
    };

    const result = await sendBugReport(payload);
    if (!result.success) {
      if (result?.data?.code === 413) {
        setSubmitStatus({
          type: 'error',
          message: result.data.message,
        });
      } else if (result?.data?.message === 'Entered epic-id is invalid, enter valid epic-id.') {
        setSubmitStatus({
          type: 'error',
          message: 'Entered Epic is invalid. Please enter a valid Epic ID',
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: 'Failed to submit bug report. Please try again.',
        });
      }

      setSubmitting(false);
      return;
    }

    // SUCCESS — API response
    setSubmitStatus({
      type: 'success',
      message: result.data.message,
      issueKey: result.data.issueKey,
      jiraUrl: result.data.jiraUrl,
    });

    setSubmitting(false);
  };
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority('Low');
    setEpicId('');
    setAttachments([]);
    setUploading(false);
    setSubmitting(false);
    setSubmitStatus(null);
  };
  const handleReportAnother = () => {
    resetForm();
  };

  return (
    <Dialog open={open} onClose={() => dispatch(closeBugReportModal())} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Report a Bug</DialogTitle>

      <DialogContent dividers>
        {submitStatus && (
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: submitStatus.type === 'success' ? 'green' : 'red',
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {submitStatus.message}
            </Typography>

            {submitStatus.type === 'success' && submitStatus.jiraUrl && (
              <Typography sx={{ fontSize: '0.9rem' }}>
                Jira Ticket:{' '}
                <Link
                  href={submitStatus.jiraUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'info.main', fontWeight: 500 }}
                >
                  {submitStatus.issueKey}
                </Link>
              </Typography>
            )}
          </Box>
        )}
        {/* TITLE */}
        <TextField
          label="Title"
          fullWidth
          required
          sx={{ mb: 2 }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        {/* DESCRIPTION */}
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={4}
          sx={{ mb: 2 }}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* PRIORITY */}
        <TextField
          select
          label="Priority"
          fullWidth
          sx={{ mb: 2 }}
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <MenuItem value="Critical">Critical</MenuItem>
          <MenuItem value="High">High</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Low">Low</MenuItem>
        </TextField>

        {/* EPIC */}
        <TextField
          label="Epic"
          fullWidth
          sx={{ mb: 2 }}
          value={epicId}
          onChange={(e) => setEpicId(e.target.value)}
          placeholder="Epic(optional)"
        />

        {/* UPLOAD SECTION */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 1 }}>Add Attachments</Typography>

          <Button
            variant="contained"
            component="label"
            sx={{ textTransform: 'none', mb: 1, gap: 1, fontWeight: 'bold' }}
          >
            <CloudUploadIcon />
            {uploading ? 'Uploading…' : 'Upload'}
            <input hidden multiple type="file" onChange={handleFileUpload} />
          </Button>

          {/* Uploading progress bar */}
          {uploading && <LinearProgress sx={{ mb: 2 }} />}

          {/* uploaded files */}
          {attachments.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                mb: 1,
                bgcolor: 'custom.surfaceAlt',
              }}
            >
              <Typography sx={{ fontSize: '0.85rem' }}>{file.name}</Typography>

              <IconButton
                color="error"
                onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={() => dispatch(closeBugReportModal())}>
          Cancel
        </Button>

        {submitStatus?.type === 'success' ? (
          <Button
            onClick={handleReportAnother}
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.light' },
            }}
          >
            Report Another Bug
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.light' },
              textTransform: 'none',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
